import { useCallback, useRef, useState } from 'react';
import { ImageFormat } from '../../types/media';
import { acceptAttributeForFormat, FORMAT_LABELS } from '../../config/conversions';
import styles from './FileUpload.module.css';

interface FileUploadProps {
  sourceFormat: ImageFormat;
  file: File | null;
  previewUrl: string | null;
  disabled?: boolean;
  onFileSelect: (file: File | null) => void;
}

export default function FileUpload({
  sourceFormat,
  file,
  previewUrl,
  disabled,
  onFileSelect,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const accept = acceptAttributeForFormat(sourceFormat);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files?.length || disabled) return;
      onFileSelect(files[0]);
    },
    [disabled, onFileSelect],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback(() => setDragOver(false), []);

  const showPreview = previewUrl && sourceFormat !== 'svg' && !file?.type.includes('svg');

  return (
    <div className={styles.wrapper}>
      <div
        className={`${styles.dropzone} ${dragOver ? styles.dragOver : ''} ${
          file ? styles.hasFile : ''
        }`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        aria-label={`Upload ${FORMAT_LABELS[sourceFormat]} file`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className={styles.hiddenInput}
          disabled={disabled}
          onChange={(e) => handleFiles(e.target.files)}
        />

        {file ? (
          <div className={styles.fileInfo}>
            {showPreview && <img src={previewUrl} alt="Preview" className={styles.preview} />}
            <div>
              <p className={styles.fileName}>{file.name}</p>
              <p className={styles.fileSize}>{(file.size / 1024).toFixed(1)} KB</p>
            </div>
            <button
              type="button"
              className={styles.clearBtn}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onFileSelect(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
            >
              Remove
            </button>
          </div>
        ) : (
          <>
            <span className={styles.icon} aria-hidden>
              ↑
            </span>
            <p className={styles.title}>Drag & drop your {FORMAT_LABELS[sourceFormat]} here</p>
            <p className={styles.hint}>or click to browse</p>
          </>
        )}
      </div>
    </div>
  );
}
